/* tslint:disable:max-line-length */
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TaskManagerState} from '../../store/task-manager.reducer';
import {getStatusList} from '../../store/task-manager.selector';
import {QueryProvider} from '@shared/queries/query-provider';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TimeZoneService} from '@core/services/time-zone.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo, hasFeature} from '@shared/store/selectors/security.selector';
import {MessageBarNotificationModel} from '@shared/models/message-bar-data.model';
import {select, Store} from '@ngrx/store';
import {TicketOwner, TicketReviewer} from '../../constants/task-manager.constants';
import {DateUtility} from '@shared/utility/date-utility';
import {addDays, addMonths, addWeeks} from '@progress/kendo-date-math';

@Component({
    standalone: false,
    selector: 'particle-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss']
})
export class TaskDetailComponent implements OnInit, OnDestroy {
  notification: MessageBarNotificationModel;
  taskDetailForm: FormGroup;
  taskDetailFormErrors: any;
  statusList$: any;
  boardList$: any;
  isClose = false;
  boardQueryKey: string;
  @Input() card: any;
  dynamicData: any;
  unsubscribe$: Subject<void>;
  dueDateEnabled = false;
  startDateEnabled = false;
  isAddCard: boolean;
  userInfo;
  isSupportUser;
  statusHidden = false;
  addCard = false;
  addCardSet = false;
  userInfoSet = false;
  hasFeature$: Observable<any>;
  isSubTask = false;
  hasPermission = true;
  hasPermissionForChangeStatus = true;
  daysDifferenceType = [
    {
      displayText: 'Day(s)',
      key: 'Days'
    },
    {
      displayText: 'Week(s)',
      key: 'Weeks'
    },
    {
      displayText: 'Month(s)',
      key: 'Months'
    }
  ];
  numbers = [1, 2, 3, 4, 5, 6, 7];
  relativeDueDateEnabled = false;
  relativeStartDateEnabled = false;

  constructor(private formBuilder: FormBuilder,
              private taskManagerStore: Store<TaskManagerState>,
              private queryStore: Store<IQueryState>,
              private timeZoneService: TimeZoneService,
              private securityStore: Store<SecurityState>
  ) {
    this.boardQueryKey = 'allBoards';
    this.unsubscribe$ = new Subject<void>();
    this.taskDetailForm = this.formBuilder.group({
      board: [Validators.required],
      status: [Validators.required],
      title: [Validators.required],
      description: [],
      taskStartDate: [Validators.required],
      dueDate: [],
      relativeStartDate: this.formBuilder.group({
        unit: [],
        period: []
      }),
      relativeDueDate: this.formBuilder.group({
        unit: [],
        period: []
      })
    });
    this.taskDetailFormErrors = {
      status: {},
      title: {},
      description: {}
    };
  }

  private static calculateDate(unit: number, period: string, date: Date): Date {
    switch (period) {
      case 'days':
        return addDays(date, unit);
      case 'weeks':
        return addWeeks(date, unit);
      case 'months':
        return addMonths(date, unit);
    }
  }

  private static getPeriod(period: string): { key: string, displayText: string } {
    switch (period) {
      case 'days':
        return {
          key: 'Days',
          displayText: 'Day(s)'
        };
      case 'weeks':
        return {
          displayText: 'Week(s)',
          key: 'Weeks'
        };
      case 'months':
        return {
          displayText: 'Month(s)',
          key: 'Months'
        };
    }
  }

  compareFunction(o1: any, o2: any) {
    return (o1.key === o2.key && o1.displayText === o2.displayText);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature), takeUntil(this.unsubscribe$));
    this.securityStore.pipe(select(getUserInfo)).subscribe((userInfo => {
      this.userInfo = userInfo;
      console.log('Received at userInfo');
      if (this.userInfo) {
        this.isSupportUser = userInfo.companyId.toLowerCase() === 'orbitaxsupport'; // TODO: Comment left by advise from Masud vai
        this.userInfoSet = true;
        this.trySetHiddenStatus();
      }
    }));
    if (this.dynamicData && this.dynamicData.card) {
      this.card = this.dynamicData.card;
      this.isSubTask = this.card?.parentTicket !== null;
      this.startDateEnabled = !!(this.card.startDate || this.dynamicData.startDate) || !!(this.card.taskStartDate || this.dynamicData.taskStartDate);
      this.dueDateEnabled = !!(this.card.dueDate || this.dynamicData.dueDate);
      this.setPermission();
    } else {
      this.addCard = true;
    }

    this.addCardSet = true;
    this.trySetHiddenStatus();
    this.taskDetailForm.get('board').setValue(this.dynamicData.board);
    this.taskDetailForm.get('status').setValue(this.card ? this.card.status : '');
    const defaultName = this.card && this.card.title ? this.card.title : (((!this.card) || (this.card && !this.card.title)) ? this.dynamicData.defaultName : '');
    this.taskDetailForm.get('title').setValue(defaultName);
    this.taskDetailForm.get('description').setValue(this.card ? this.strip(this.card.description) : '');
    let startDate = this.card && this.card.taskStartDate ? this.timeZoneService.getDisplayDate(this.card.taskStartDate) : (((!this.card) || (this.card && !this.card.taskStartDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.taskStartDate)) : '');
    if (!this.isValidDate(startDate)) {
      startDate = this.card && this.card.startDate ? this.timeZoneService.getDisplayDate(this.card.startDate) : (((!this.card) || (this.card && !this.card.startDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.startDate)) : '');
    }
    this.taskDetailForm.get('taskStartDate').setValue(startDate);
    const dueDate = this.card && this.card.dueDate ? this.timeZoneService.getDisplayDate(this.card.dueDate) : (((!this.card) || (this.card && !this.card.dueDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.dueDate)) : '');
    this.taskDetailForm.get('dueDate').setValue(dueDate);

    this.taskDetailForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.onFormValuesChanged();
    });

    this.statusList$ = this.taskManagerStore.select(getStatusList);
    this.statusList$.pipe(takeUntil(this.unsubscribe$)).subscribe(statusList => {
      if (statusList && this.taskDetailForm.controls['status'].value === '') {
        this.taskDetailForm.get('status').setValue(statusList[0].status);
      }
    });
    this.boardList$ = this.queryStore.pipe(select(getQueryResponse, this.boardQueryKey));
    this.boardList$.pipe(takeUntil(this.unsubscribe$)).subscribe(state => {
      console.log(state);
      if (state) {
        let currentBoard = state.items[0];
        if (this.dynamicData && this.dynamicData.board) {
          currentBoard = state.items.find(o => o.id === this.dynamicData.board.id);
        }
        this.taskDetailForm.get('board').setValue(currentBoard);
      }
    });
    QueryProvider.invokeQuery(this.queryStore, this.boardQueryKey, {});
    console.log('[TaskDetailComponent]', this.card);
    if (this.card?.fieldValues) {
      const relativeDate = this.getUnitAndPeriod();
      this.taskDetailForm.get('relativeStartDate.unit').setValue(relativeDate['RelativeStartDate']?.unit);
      this.taskDetailForm.get('relativeStartDate.period').setValue(relativeDate['RelativeStartDate']?.period);
      this.taskDetailForm.get('relativeDueDate.unit').setValue(relativeDate['RelativeDueDate']?.unit);
      this.taskDetailForm.get('relativeDueDate.period').setValue(relativeDate['RelativeDueDate']?.period);
    }
    if (this.isSubTask) {
      this.listenFormControlChange('relativeStartDate', 'taskStartDate');
      this.listenFormControlChange('relativeDueDate', 'dueDate');
    }
  }

  strip(description: any) {
    const document = new DOMParser().parseFromString(description, 'text/html');
    return document.body.textContent || '';
  }

  trySetHiddenStatus() {
    if (this.userInfoSet && this.addCardSet) {
      if (this.addCard) {
        this.statusHidden = true;
      } else if (!this.isSupportUser && this.card.scopeInt === 3) {
        this.statusHidden = true;
      }
    }
  }

  onFormValuesChanged() {
    this.getDetailFormValueChangedData();
    console.log('@@@@ Detail Form Value Changed @@@@', this.dynamicData.detailFormValueChanged);
    let hasError = false;
    for (const field in this.taskDetailFormErrors) {
      if (!this.taskDetailFormErrors.hasOwnProperty(field)) {
        continue;
      }
      this.taskDetailFormErrors[field] = {};
      const control = this.taskDetailForm.get(field);
      if (control && control.dirty && !control.valid) {
        this.taskDetailFormErrors[field] = control.errors;
        hasError = true;
      }
    }
    if (!hasError) {
      this.dynamicData.card = {
        ...this.dynamicData.card,
        status: this.taskDetailForm.get('status').value,
        title: this.taskDetailForm.get('title').value,
        description: this.taskDetailForm.get('description').value,
        taskStartDate: this.startDateEnabled ?
          (DateUtility.isValidDateStringOrObject(this.taskDetailForm.get('taskStartDate').value)
            ? this.getDisplayDate('taskStartDate')
            : null)
          : null,
        startDate: this.startDateEnabled ?
          (DateUtility.isValidDateStringOrObject(this.taskDetailForm.get('taskStartDate').value)
            ? this.getDisplayDate('taskStartDate')
            : null)
          : null,
        dueDate: this.dueDateEnabled ?
          (DateUtility.isValidDateStringOrObject(this.taskDetailForm.get('dueDate').value)
            ? this.getDisplayDate('dueDate')
            : null) :
          null,
        relativeStartDate: this.getRelativeDate(this.relativeStartDateEnabled, 'relativeStartDate'),
        relativeDueDate: this.getRelativeDate(this.relativeDueDateEnabled, 'relativeDueDate'),
      };
      if (this.taskDetailForm.get('board').value) {
        this.dynamicData.board = this.taskDetailForm.get('board').value;
      }
    }
  }

  close() {
    this.isClose = true;
  }

  isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  private getDisplayDate(controlName: string) {
    return this.timeZoneService
      .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(this.taskDetailForm.get(controlName).value));
  }

  private getDetailFormValueChangedData() {
    if (this.taskDetailForm.pristine) {
      if (this.startDateEnabled && this.dueDateEnabled) {
        this.dynamicData.detailFormValueChanged = false;
      } else {
        this.dynamicData.detailFormValueChanged = true;
      }
    } else {
      this.dynamicData.detailFormValueChanged = !this.taskDetailForm.pristine;
    }
  }

  private setPermission() {
    console.log('[Card]', this.card);
    this.hasPermission = this.card && this.card.hasPermission;
    this.hasPermissionForChangeStatus = this.card
      && (TicketOwner <= this.card.permissionLevel && this.card.permissionLevel <= TicketReviewer);
  }

  private getRelativeDate(enable: boolean, controlName: string): string | null {
    if (!enable) {
      return null;
    }
    const value = this.taskDetailForm.get(controlName).value;
    const isNullish = Object.values(value).some(o => o === null || o === undefined);
    return isNullish ? null : `${value.unit.toString()} ${value.period.key}`;
  }

  private getUnitAndPeriod() {
    const fieldValues = this.card.fieldValues.reduce((obj, value) => {
        return {
          ...obj,
          [value.Key]: value.Value
        };
      }
      , {});
    const returnValues = {};
    const relativeStartDate = fieldValues['RelativeStartDate'];
    const relativeDueDate = fieldValues['RelativeDueDate'];

    if (relativeStartDate) {
      this.relativeStartDateEnabled = true;
      const values = relativeStartDate.split(' ');
      const value = {
        unit: parseInt(values[0], 10),
        period: TaskDetailComponent.getPeriod(values[1].toLowerCase())
      };
      Object.assign(returnValues, {RelativeStartDate: value});
    }
    if (relativeDueDate) {
      this.relativeDueDateEnabled = true;
      const values = relativeDueDate.split(' ');
      const value = {
        unit: parseInt(values[0], 10),
        period: TaskDetailComponent.getPeriod(values[1].toLowerCase())
      };
      Object.assign(returnValues, {RelativeDueDate: value});
    }
    return returnValues;
  }

  private listenFormControlChange(control: string, targetControl: string) {
    this.taskDetailForm.get(control).valueChanges.subscribe((values) => {
      const unit = values.unit;
      const period = values.period?.key?.toLowerCase();
      if (unit && period) {
        const date = this.getCalculatedDate(targetControl, unit, period);
        if (date) {
          this.taskDetailForm.get(targetControl).setValue(date);
        }
      }
    });
  }

  private getCalculatedDate(targetControl: string, unit: number, period: string): Date | null {
    switch (targetControl) {
      case 'taskStartDate':
        const startDate = this.card.parentTicket?.startDate;
        if (startDate) {
          const updatedDate = TaskDetailComponent.calculateDate(unit, period, new Date(startDate));
          const date = this.timeZoneService
            .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(updatedDate));
          if (date) {
            this.startDateEnabled = true;
          }
          return date;
        }
        return null;
      case 'dueDate':
        const dueDate = this.card.parentTicket?.dueDate;
        if (dueDate) {
          const updatedDate = TaskDetailComponent.calculateDate(-unit, period, new Date(dueDate));
          const date = this.timeZoneService
            .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(updatedDate));
          if (date) {
            this.dueDateEnabled = true;
          }
          return date;
        }
        return null;
      default:
        return null;
    }
  }
}
